const apiBaseStorageKey = 'vault-kosmos:api-base-url';
const defaultConnectorBase = 'http://127.0.0.1:8787';

export interface JsonResponse<T> {
  response: Response;
  data: T;
}

export function apiBaseUrl(): string {
  const stored = storedApiBaseUrl();
  if (stored) return stripTrailingSlash(stored);
  return isLocalFrontend() ? '' : defaultConnectorBase;
}

export function apiEndpoint(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const base = apiBaseUrl();
  return base ? `${base}${normalizedPath}` : normalizedPath;
}

export function websocketEndpoint(): string {
  const base = apiBaseUrl();
  if (base) {
    try {
      const url = new URL(base);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = '/ws';
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch {
      return 'ws://127.0.0.1:8787/ws';
    }
  }

  const host = window.location.hostname || '127.0.0.1';
  const protocol = window.location.protocol === 'https:' && !isLoopbackHost(host) ? 'wss' : 'ws';
  return `${protocol}://${host}:8787/ws`;
}

export function shouldUseStaticDemo(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get('demo') === '1') return true;
  if (params.get('local') === '1') return false;
  return !isLocalFrontend();
}

export async function fetchJson<T>(path: string, init?: RequestInit): Promise<JsonResponse<T>> {
  const response = await fetch(apiEndpoint(path), init);
  const raw = await response.text();
  const contentType = response.headers.get('content-type') ?? '';

  if (!contentType.toLowerCase().includes('application/json')) {
    throw new Error(nonJsonError(response, raw));
  }

  try {
    return {
      response,
      data: JSON.parse(raw) as T
    };
  } catch {
    throw new Error(`Der lokale Vault-Kosmos-Connector hat ungültiges JSON geliefert (${response.status}).`);
  }
}

function nonJsonError(response: Response, raw: string): string {
  const preview = raw.slice(0, 80).replace(/\s+/g, ' ').trim();
  const base = apiBaseUrl() || 'lokaler Dev-Proxy';
  return [
    `Der lokale Vault-Kosmos-Connector antwortet nicht mit JSON (${response.status}).`,
    `API-Ziel: ${base}.`,
    'Prüfe, ob der lokale Server läuft und nicht nur das statische Frontend ausgeliefert wird.',
    preview ? `Antwort beginnt mit: ${preview}` : ''
  ]
    .filter(Boolean)
    .join(' ');
}

export function isLocalFrontend(): boolean {
  return isLoopbackHost(window.location.hostname);
}

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function storedApiBaseUrl(): string | undefined {
  try {
    return window.localStorage.getItem(apiBaseStorageKey)?.trim() || undefined;
  } catch {
    return undefined;
  }
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
